import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';

import AddCallIcon from '@mui/icons-material/AddIcCall';
import { makeStyles } from '@mui/styles';

import {
  Enum,
  ProgramTaskReviewStatus,
  ProgramTaskStatus,
} from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  PaginationType,
  PodConfig,
  PodRule,
  ProgramTask,
} from '@vestahealthcare/common/models';
import {
  EMPTY,
  NO_DAY_NO_TIME_DATE_FORMAT_DO,
} from '@vestahealthcare/common/utils/constants';

import {
  Actions,
  BackgroundColors,
  CollapseIcon,
  Colors,
  Fonts,
  IconButton,
  StatusIndicator,
  Table,
  TableItem,
  UpdatedBy,
} from 'styleguide-v2';

import Session from 'dash/src/services/SessionServices';

import { MemberCCMTaskAction } from './types/MemberCCMTaskAction';

const useStyles = makeStyles({
  table: {
    '&& tbody': {
      '& td': {
        padding: '6px',
        fontSize: `calc(${Fonts.fontSize} * 0.75)`,
      },
      '& a': {
        fontSize: `calc(${Fonts.fontSize} * 0.75)`,
      },
      '& > tr.detail > td': {
        background: Colors.white,
        padding: '0',
      },
    },
  },
  noPaddingTable: {
    paddingTop: 0,

    '&& thead': {
      display: 'none',
    },
    '&& tbody': {
      borderRadius: 0,
      boxShadow: 'none',

      '& tr td': {
        backgroundColor: `${BackgroundColors.lighterGray}!important`,
      },
    },
    '&& tbody tbody': {
      borderLeft: 0,
      borderRight: 0,
    },
  },
});

export type TaskTableModel = ProgramTask &
  TableItem & {
    actionLoading?: boolean;
  };

type Props = {
  child?: boolean;
  data: {
    pods: PodConfig[];
    taskDefinitions: PodRule[];
  };
  onChangePage?: (page: number) => void;
  onChangePageSize?: (pageSize: number) => void;
  onLogCall?: (task: ProgramTask) => void;
  onTaskAction?: (
    action: Selectable,
    event: ProgramTask,
    index: number,
  ) => void;
  pagination?: PaginationType;
  tasks: ProgramTask[];
};

const MEMBER_CCM_TASK_COLUMNS = (
  { child, data, onLogCall, onTaskAction }: Props,
  onToggleDetail: (task: TaskTableModel, index: number) => void,
) => [
  {
    headerName: translate('memberCCMTasks.table.date'),
    component: ({ taskDate }: ProgramTask) =>
      taskDate?.format(NO_DAY_NO_TIME_DATE_FORMAT_DO) || EMPTY,
    width: 150,
  },
  {
    headerName: translate('memberCCMTasks.table.type'),
    field: 'name',
  },
  {
    headerName: translate('memberCCMTasks.table.status'),
    component: ({ adminStatus, shouldShowReviewStatus, status }: ProgramTask) =>
      status ? (
        <StatusIndicator
          status={status.toString()}
          subtitle={
            shouldShowReviewStatus
              ? (adminStatus || ProgramTaskReviewStatus.NOT_REVIEWED).toString()
              : ''
          }
          color={ProgramTaskStatus.getColor(status)}
        />
      ) : (
        EMPTY
      ),
    width: 200,
  },
  {
    headerName: translate('memberCCMTasks.table.statusUpdatedBy'),
    component: ({ statusUpdatedAt, statusUpdatedBy }: ProgramTask) =>
      statusUpdatedAt ? (
        <UpdatedBy date={statusUpdatedAt} user={statusUpdatedBy?.fullName} />
      ) : (
        EMPTY
      ),
  },
  {
    headerName: translate('memberCCMTasks.table.assignee'),
    field: 'assignee.fullName',
    width: 200,
  },
  {
    headerName: ' ',
    component: (task: TaskTableModel, index: number) => {
      const actions = MemberCCMTaskAction.asActionsArray(
        task,
        data.taskDefinitions?.find(({ name }) => name === task.name),
        data.pods?.find(({ id }) => id === task.member?.pod?.id),
        Session.actingUser,
      );

      return (
        <div className="flex gap spaced">
          {onLogCall &&
          ProgramTaskStatus.asArrayOpened.includes(task.status) ? (
            <IconButton
              size="small"
              onClick={(evt) => {
                evt.stopPropagation();
                onLogCall(task);
              }}
              tooltip={translate('ccmTasks.table.logCall')}
            >
              <AddCallIcon />
            </IconButton>
          ) : (
            <div style={{ width: '3.3rem' }} />
          )}
          {onTaskAction && actions?.length ? (
            <Actions
              actions={Enum.toSelectable(actions)}
              data-cy={`ccm-task-item-${index}-actions`}
              loading={task.actionLoading}
              onActionSelected={(action) => onTaskAction(action, task, index)}
            />
          ) : (
            <div style={{ width: '3.3rem' }} />
          )}
          {!child && task.children?.length ? (
            <IconButton
              size="small"
              onClick={(evt) => {
                evt.stopPropagation();
                onToggleDetail(task, index);
              }}
            >
              <CollapseIcon
                open={!!task.open}
                data-cy={`ccm-task-item-${index}-collapse`}
              />
            </IconButton>
          ) : (
            <div style={{ width: '4.1rem', height: '4.1rem' }} />
          )}
        </div>
      );
    },
    width: 150,
  },
];

export const MemberCCMTasksTable = (props: Props) => {
  const styles = useStyles();
  const [data, setData] = useState<TaskTableModel[]>([]);

  const onToggleDetail = (item: TaskTableModel) => {
    item.open = !item.open;
    if (item.open) {
      item.detail = (
        <MemberCCMTasksTable
          child
          data={props.data}
          tasks={item.children || []}
        />
      );
    } else {
      delete item.detail;
    }
    setData([...data]);
  };

  const columns = useMemo(
    () => MEMBER_CCM_TASK_COLUMNS(props, onToggleDetail),
    [props.onLogCall, props.data.taskDefinitions],
  );

  useEffect(() => setData(props.tasks), [props.data]);

  return (
    <Table
      className={classNames(styles.table, props.child && styles.noPaddingTable)}
      config={{
        columns,
        compact: true,
        data,
        detail: true,
        defaultPageSize: 25,
        paginationOptions: props.child ? [-1] : undefined,
        pagination: props.pagination,
      }}
      empty
      fixedHeader
      onChangePage={props.onChangePage}
      onChangeRowsPerPage={props.onChangePageSize}
    />
  );
};

export default MemberCCMTasksTable;
